import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Wifi, WifiOff } from "lucide-react";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GREEN = "#1A7A44";

type Status = "online" | "offline" | "back";

/**
 * How long the offline line stays. It sits over the header, where the search
 * and the account live, so it says its piece and steps aside; a click closes
 * it sooner. It comes back only when the connection changes again.
 */
const OFFLINE_SHOWN_MS = 6000;

/**
 * A calm line at the top when the connection drops (owner, 2026-10-26, idea
 * 78 in the suggestions lab).
 *
 * The page stays as it is: no full-screen error, no red. What is on screen can
 * still be read, and the bucket is kept in the browser, so the banner says so.
 * When the connection returns it says "Back online" for a moment and goes.
 *
 * Browser only: during SSR there is no connection to speak of, so it renders
 * nothing until the first effect has looked at `navigator.onLine`.
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("online");
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const later = (ms: number, then: () => void) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(then, ms);
    };
    const goOffline = () => {
      setHidden(false);
      setStatus("offline");
      later(OFFLINE_SHOWN_MS, () => setHidden(true));
    };
    const goOnline = () => {
      setHidden(false);
      setStatus((s) => (s === "offline" ? "back" : s));
      later(2500, () => setStatus("online"));
    };
    if (navigator.onLine === false) goOffline();
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (status === "online" || hidden) return null;

  const offline = status === "offline";
  return (
    <div
      role="status"
      aria-live="polite"
      className="mj-fade-in pointer-events-none fixed inset-x-0 top-[max(10px,env(safe-area-inset-top))] z-[10001] flex justify-center px-3"
    >
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label={`${offline ? t("offline.title") : t("offline.back")}. ${t("offline.dismiss_hint")}`}
        className="pointer-events-auto flex max-w-[460px] items-center gap-2.5 rounded-[18px] bg-white px-3.5 py-2.5 text-start"
        style={{ boxShadow: "0 0 0 1px rgba(17,14,57,0.10), 0 14px 28px -12px rgba(17,14,57,0.30)" }}
      >
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
          style={{ background: offline ? "rgba(17,14,57,0.06)" : "rgba(31,168,85,0.14)", color: offline ? NAVY : GREEN }}
          aria-hidden
        >
          {offline ? <WifiOff size={16} strokeWidth={2.4} /> : <Wifi size={16} strokeWidth={2.4} />}
        </span>
        <span className="min-w-0">
          <span className="block text-[13.5px] font-bold" style={{ color: NAVY }}>
            {offline ? t("offline.title") : t("offline.back")}
          </span>
          {offline ? (
            <span className="block text-[12.5px] font-medium leading-snug" style={{ color: MUTED }}>
              {t("offline.body")}
            </span>
          ) : null}
        </span>
      </button>
    </div>
  );
}
